import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { HiOutlineLockClosed, HiOutlineEye, HiOutlineEyeSlash, HiOutlineKey } from 'react-icons/hi2';
import { authService } from '../services';
import './ChangePassword.css';

const ChangePassword = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false,
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  const [passwordStrength, setPasswordStrength] = useState({
    isStrong: false,
    hasMinLength: false,
    hasUpper: false,
    hasLower: false,
    hasNumber: false,
    hasSpecial: false,
  });

  const checkStrength = (password) => {
    const strength = {
      hasMinLength: password.length >= 6,
      hasUpper: /[A-Z]/.test(password),
      hasLower: /[a-z]/.test(password),
      hasNumber: /[0-9]/.test(password),
      hasSpecial: /[!@#$%^&*(),.?":{}|<>]/.test(password),
    };
    strength.isStrong = strength.hasMinLength && strength.hasUpper && strength.hasLower && strength.hasNumber && strength.hasSpecial;
    setPasswordStrength(strength);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });

    if (name === 'newPassword') {
      checkStrength(value);
    }

    setError('');
    setSuccess('');
  };


  const togglePasswordVisibility = (field) => {
    setShowPasswords({
      ...showPasswords,
      [field]: !showPasswords[field],
    });
  };

  const validateForm = () => {
    if (!formData.currentPassword.trim()) {
      setError('Current password is required');
      return false;
    }

    if (!formData.newPassword.trim()) {
      setError('New password is required');
      return false;
    }

    if (!passwordStrength.isStrong) {
      setError('New password does not meet strength requirements');
      return false;
    }

    if (formData.newPassword !== formData.confirmPassword) {
      setError('Confirm password does not match with new password');
      return false;
    }

    if (formData.currentPassword === formData.newPassword) {
      setError('New password must be different from current password');
      return false;
    }

    return true;
  };


  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    if (!validateForm()) {
      setLoading(false);
      return;
    }

    try {
      const response = await authService.changePassword(
        formData.currentPassword,
        formData.newPassword
      );

      if (response.Success || response.success) {
        setSuccess(response.Message || response.message || 'Password changed successfully!');
        setFormData({
          currentPassword: '',
          newPassword: '',
          confirmPassword: '',
        });
        setPasswordStrength({
          isStrong: false,
          hasMinLength: false,
          hasUpper: false,
          hasLower: false,
          hasNumber: false,
          hasSpecial: false,
        });


        // Redirect after 2 seconds
        setTimeout(() => {
          navigate('/home');
        }, 2000);
      } else {
        setError(response.Error || response.error || response.Message || response.message || 'Failed to change password');
      }
    } catch (err) {
      setError(
        err.response?.data?.Error ||
        err.response?.data?.error ||
        err.response?.data?.Message ||
        err.response?.data?.message ||
        err.message ||
        'Failed to change password. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="change-password-page">
      <div className="change-password-container">
        <div className="change-password-header">
          <div className="header-icon">
            <HiOutlineLockClosed />
          </div>
          <div>
            <h1>Change Password</h1>
            <p>Update your account password to keep it secure</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="change-password-form">
          {error && <div className="error-message">{error}</div>}
          {success && <div className="success-message">{success}</div>}

          <div className="form-group">
            <label htmlFor="currentPassword">
              <HiOutlineLockClosed className="label-icon" />
              Current Password <span className="required">*</span>
            </label>
            <div className="password-input-wrapper">
              <input
                type={showPasswords.current ? 'text' : 'password'}
                id="currentPassword"
                name="currentPassword"
                value={formData.currentPassword}
                onChange={handleChange}
                placeholder="Enter your current password"
                required
                className="form-input password-input"
              />
              <div className="input-icon-left">
                <HiOutlineKey />
              </div>
              <button
                type="button"
                className="password-toggle"
                onClick={() => togglePasswordVisibility('current')}
                tabIndex="-1"
                aria-label={showPasswords.current ? 'Hide password' : 'Show password'}
              >
                {showPasswords.current ? <HiOutlineEyeSlash /> : <HiOutlineEye />}
              </button>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="newPassword">
              <HiOutlineLockClosed className="label-icon" />
              New Password <span className="required">*</span>
            </label>
            <div className="password-input-wrapper">
              <input
                type={showPasswords.new ? 'text' : 'password'}
                id="newPassword"
                name="newPassword"
                value={formData.newPassword}
                onChange={handleChange}
                placeholder="Enter your new password"
                required
                className={`form-input password-input ${formData.newPassword ? (passwordStrength.isStrong && formData.newPassword !== formData.currentPassword ? 'input-strong' : 'input-weak') : ''}`}
              />
              <div className="input-icon-left">
                <HiOutlineKey />
              </div>
              <button
                type="button"
                className="password-toggle"
                onClick={() => togglePasswordVisibility('new')}
                tabIndex="-1"
                aria-label={showPasswords.new ? 'Hide password' : 'Show password'}
              >
                {showPasswords.new ? <HiOutlineEyeSlash /> : <HiOutlineEye />}
              </button>
            </div>
            {formData.newPassword && formData.newPassword === formData.currentPassword && (
              <div className="password-strength-alert alert-weak">
                <span className="strength-status">Same as current password</span>
              </div>
            )}
          </div>


          <div className="form-group">
            <label htmlFor="confirmPassword">
              <HiOutlineLockClosed className="label-icon" />
              Confirm New Password <span className="required">*</span>
            </label>
            <div className="password-input-wrapper">
              <input
                type={showPasswords.confirm ? 'text' : 'password'}
                id="confirmPassword"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                placeholder="Confirm your new password"
                required
                className={`form-input password-input ${formData.confirmPassword ? (formData.confirmPassword === formData.newPassword ? 'input-strong' : 'input-weak') : ''}`}
              />
              <div className="input-icon-left">
                <HiOutlineKey />
              </div>
              <button
                type="button"
                className="password-toggle"
                onClick={() => togglePasswordVisibility('confirm')}
                tabIndex="-1"
                aria-label={showPasswords.confirm ? 'Hide password' : 'Show password'}
              >
                {showPasswords.confirm ? <HiOutlineEyeSlash /> : <HiOutlineEye />}
              </button>
            </div>
          </div>


          <div className="form-actions">
            <button
              type="button"
              className="btn-secondary"
              onClick={() => navigate('/home')}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={loading}
            >
              {loading ? 'Changing Password...' : 'Change Password'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ChangePassword;

